import React, { useState } from "react";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { PageHeader, DataTable, Modal, StatusBadge, KpiCard, Button, Field, Input, Select, Textarea, Tabs } from "../components/shared";
import { BulkUpload } from "../components/BulkUpload";
import { money, num, fmtDate } from "../lib/api";
import { Layers, IndianRupee, Boxes, Upload, PackagePlus, Trash2 } from "lucide-react";

export default function FabricStock() {
  const { data, loading, reload } = useData("/fabric-lots");
  const { data: suppliers } = useData("/suppliers");
  const { data: ledger } = useData("/fabric-lots/ledger");
  const [tab, setTab] = useState("stock");
  const [showZero, setShowZero] = useState(false);
  const isZero = (l) => Math.abs(l.balance_qty || 0) < 0.005;
  const zeroCount = (data || []).filter(isZero).length;
  const visibleLots = showZero ? (data || []) : (data || []).filter((l) => !isZero(l));
  const [history, setHistory] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [edit, setEdit] = useState(null);
  const [loss, setLoss] = useState(null);
  const openLoss = (r) => setLoss({ lot: r, quantity: "", reason: "", date: new Date().toISOString().slice(0, 10) });
  const submitLoss = async (e) => {
    e.preventDefault();
    if (!(parseFloat(loss.quantity) > 0)) { toast.error("Enter a loss quantity"); return; }
    try { await save("post", `/fabric-lots/${loss.lot.id}/loss`, { quantity: parseFloat(loss.quantity) || 0, reason: loss.reason, date: loss.date }); toast.success("Shifted to normal loss"); setLoss(null); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const [gain, setGain] = useState(null);
  const openGain = (r) => setGain({ lot: r, quantity: String(Math.round(-(r.balance_qty || 0) * 100) / 100), reason: "", date: new Date().toISOString().slice(0, 10) });
  const submitGain = async (e) => {
    e.preventDefault();
    const q = parseFloat(gain.quantity) || 0;
    if (q + (gain.lot.balance_qty || 0) < -0.001) { toast.error(`Gain must be at least ${num(-(gain.lot.balance_qty || 0))}`); return; }
    try { await save("post", `/fabric-lots/${gain.lot.id}/gain`, { quantity: q, reason: gain.reason, date: gain.date }); toast.success("Gain adjusted"); setGain(null); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  const openEdit = (r) => setEdit({ ...r });
  const submitEdit = async (e) => {
    e.preventDefault();
    try { await save("put", `/fabric-lots/${edit.id}`, edit); toast.success("Fabric lot updated"); setEdit(null); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const delLot = async (r) => {
    if (!isAdmin) return;
    if (!window.confirm(`This will permanently delete Fabric Purchase / Lot ${r.lot_number} and its purchase bill & ledger. Do you want to continue?`)) return;
    const reason = window.prompt("Reason for deletion (recorded in the audit log):", "");
    if (reason === null) return;
    try { await api.delete(`/fabric-lots/${r.id}`, { params: { reason } }); toast.success("Fabric purchase deleted"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const openPurchase = () => setPurchase({ add_to_existing: false, receiving_date: new Date().toISOString().slice(0, 10), supplier_name: "", bill_number: "", fabric_type: "", colour: "", rolls: "", quantity: "", unit: "MTR", rate: "", lot_number: "", remarks: "" });
  const pickLot = (lotNumber) => {
    const l = (data || []).find((x) => x.lot_number === lotNumber);
    setPurchase((p) => ({ ...p, lot_number: lotNumber, fabric_type: l?.fabric_type || p.fabric_type, colour: l?.colour || p.colour, unit: l?.unit || p.unit, rate: l?.rate || p.rate }));
  };
  const submitPurchase = async (e) => {
    e.preventDefault();
    if (!parseFloat(purchase.quantity)) { toast.error("Enter quantity"); return; }
    try {
      const res = await save("post", "/fabric-receipts", { ...purchase, quantity: parseFloat(purchase.quantity), rate: parseFloat(purchase.rate) || 0, rolls: parseFloat(purchase.rolls) || 0, purchase_date: purchase.receiving_date });
      toast.success(purchase.add_to_existing ? `Added to Lot ${res.lot_number}` : `Fabric purchase recorded — Lot ${res.lot_number}`); setPurchase(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openHistory = async (r) => {
    const { data: h } = await api.get(`/fabric-lots/${r.lot_number}/history`);
    setHistory(h);
  };

  const totalQty = (data || []).reduce((s, l) => s + (l.balance_qty || 0), 0);
  const totalVal = (data || []).reduce((s, l) => s + (l.balance_qty || 0) * (l.rate || 0), 0);

  const ledgerBadge = (t, isIn) => {
    const loss = /loss/i.test(t || "");
    const cls = loss ? "bg-amber-50 text-amber-700 border-amber-200" : isIn ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200";
    return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>{t}</span>;
  };
  const ledgerCols = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: "type", header: "Type", render: (r) => ledgerBadge(r.type, r.qty_in > 0) },
    { key: "lot_number", header: "Lot No", mono: true, render: (r) => <span className="font-semibold text-blue-800">{r.lot_number}</span> },
    { key: "fabric_type", header: "Fabric", render: (r) => r.fabric_type || "—" },
    { key: "colour", header: "Colour", render: (r) => r.colour || "—" },
    { key: "qty_in", header: "In", align: "right", render: (r) => r.qty_in ? <span className="font-semibold text-emerald-700">+{num(r.qty_in)}</span> : <span className="text-slate-300">—</span> },
    { key: "qty_out", header: "Out", align: "right", render: (r) => r.qty_out ? <span className="font-semibold text-red-600">−{num(r.qty_out)}</span> : <span className="text-slate-300">—</span> },
    { key: "balance", header: "Lot Balance", align: "right", render: (r) => <span className="font-semibold">{num(r.balance)} {r.unit || ""}</span> },
    { key: "note", header: "Note", render: (r) => r.note || "—" },
  ];

  const columns = [
    { key: "purchase_date", header: "Purchase Date", render: (r) => fmtDate(r.purchase_date) },
    { key: "bill_number", header: "Bill No" },
    { key: "supplier_name", header: "Supplier" },
    { key: "lot_number", header: "Lot No", mono: true, render: (r) => <span className="font-semibold text-blue-800">{r.lot_number}</span> },
    { key: "fabric_type", header: "Fabric" },
    { key: "colour", header: "Colour" },
    { key: "rolls", header: "Rolls", align: "right" },
    { key: "balance_qty", header: "Balance", align: "right", render: (r) => <span className={r.balance_qty > 0 ? "font-semibold text-emerald-700" : "text-slate-400"}>{num(r.balance_qty)} {r.unit}</span> },
    { key: "rate", header: "Rate", align: "right", render: (r) => num(r.rate) },
    { key: "value", header: "Balance Value", align: "right", render: (r) => money((r.balance_qty || 0) * (r.rate || 0)) },
    { key: "remarks", header: "Remarks", render: (r) => r.remarks ? <span className="text-slate-600 whitespace-pre-wrap break-words max-w-[14rem] inline-block" title={r.remarks}>{r.remarks}</span> : <span className="text-slate-300">—</span> },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button title="Edit" onClick={() => openEdit(r)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900" data-testid={`lot-edit-${r.id}`}>Edit</button>
        <button title="Shift to Normal Loss" onClick={() => openLoss(r)} className="px-2 py-1 text-xs text-amber-700 hover:text-amber-900" data-testid={`lot-loss-${r.id}`}>Loss</button>
        {(r.balance_qty || 0) < 0 && <button title="Adjust negative stock with gain" onClick={() => openGain(r)} className="px-2 py-1 text-xs text-emerald-700 hover:text-emerald-900 font-medium" data-testid={`lot-gain-${r.id}`}>Gain</button>}
        {isAdmin && <button title="Delete" onClick={() => delLot(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`lot-delete-${r.id}`}><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Fabric Stock" subtitle="Lot-wise real-time fabric inventory. Click a lot for full history.">
        <Button onClick={openPurchase} data-testid="fabric-record-purchase-btn"><PackagePlus size={15} /> Record Purchase</Button>
        <Button variant="secondary" onClick={() => setBulk(true)} data-testid="fabric-bulk-btn"><Upload size={15} /> Bulk Upload</Button>
      </PageHeader>
      {tab === "stock" ? (
        <div className="grid grid-cols-3 gap-4 mb-5">
          <KpiCard label="Total Balance Qty" value={num(totalQty)} icon={Layers} accent="blue" />
          <KpiCard label="Stock Value" value={money(totalVal)} icon={IndianRupee} accent="emerald" />
          <KpiCard label="Total Lots" value={num(visibleLots.length)} icon={Boxes} accent="sky" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 mb-5 max-w-lg">
          <KpiCard label="Total In (Received + Top-ups)" value={num(ledger?.totals?.in || 0)} icon={Layers} accent="emerald" />
          <KpiCard label="Total Out (Cutting + Loss)" value={num(ledger?.totals?.out || 0)} icon={Boxes} accent="red" />
        </div>
      )}
      <Tabs testid="fabric-tabs" tabs={[{ value: "stock", label: "Stock" }, { value: "ledger", label: `In / Out Ledger (${(ledger?.entries || []).length})` }]} active={tab} onChange={setTab} />
      <div className="mt-4">
        {tab === "stock" && zeroCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600 mb-3 w-fit cursor-pointer" data-testid="fabric-show-zero-label">
            <input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} data-testid="fabric-show-zero" />
            Show finished lots with 0 balance ({zeroCount} hidden)
          </label>
        )}
        {tab === "stock" && !loading && <DataTable testid="fabric-stock-table" columns={columns} rows={visibleLots} exportName="fabric-stock.csv" onRowClick={openHistory} />}
        {tab === "ledger" && <DataTable testid="fabric-ledger-table" columns={ledgerCols} rows={ledger?.entries || []} exportName="fabric-ledger.csv" />}
      </div>

      <Modal open={bulk} onClose={() => setBulk(false)} title="Bulk Upload Fabric Stock" wide>
        <BulkUpload
          endpoint="/fabric-lots/bulk"
          onDone={reload}
          headers={["Lot Number", "Supplier", "Bill Number", "Purchase Date", "Fabric Type", "Colour", "Rolls", "Quantity", "Unit", "Rate", "Remarks"]}
          sampleRow={["LOT-OPEN-001", "Vardhman Textiles", "BILL-101", "2026-06-01", "Cotton Twill", "Navy", 20, 1000, "MTR", 145, "Opening stock"]}
        />
      </Modal>

      <Modal open={!!history} onClose={() => setHistory(null)} title={`Lot History — ${history?.lot?.lot_number || ""}`} wide>
        {history && (
          <div className="space-y-5">
            <div className="grid grid-cols-4 gap-3">
              {[["Fabric", history.lot.fabric_type], ["Colour", history.lot.colour], ["Received", num(history.lot.quantity)], ["Balance", num(history.lot.balance_qty)]].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-md p-3"><p className="text-xs text-slate-500">{l}</p><p className="text-sm font-semibold mt-0.5">{v}</p></div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Stock Ledger</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr>
                    <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Note</th><th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Balance</th></tr></thead>
                  <tbody>{history.transactions.map((t) => (
                    <tr key={t.id} className="border-t">
                      <td className="px-3 py-2">{fmtDate(t.date)}</td>
                      <td className="px-3 py-2">{t.type === "NORMAL LOSS" ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Normal Loss</span> : t.type === "GAIN" ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Gain</span> : <StatusBadge status={t.type === "RECEIPT" ? "Received" : "Issued"} />}</td>
                      <td className="px-3 py-2 text-slate-500">{t.note}</td>
                      <td className={`px-3 py-2 text-right tabular ${t.qty < 0 ? "text-red-600" : "text-emerald-700"}`}>{num(t.qty)}</td>
                      <td className="px-3 py-2 text-right tabular font-medium">{num(t.balance)}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Designs Cut from this Lot</p>
              {(history.cuttings || []).length === 0 ? (
                <p className="text-sm text-slate-400">No cutting has used this lot yet.</p>
              ) : (
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr>
                      <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Design</th>
                      <th className="text-left px-3 py-2">Colour</th><th className="text-left px-3 py-2">Item</th>
                      <th className="text-left px-3 py-2">Prod Order</th><th className="text-left px-3 py-2">Sheet No</th>
                      <th className="text-right px-3 py-2">Cut Pcs</th><th className="text-right px-3 py-2">Fabric Used</th></tr></thead>
                    <tbody data-testid="lot-cuttings-table">{history.cuttings.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="px-3 py-2">{fmtDate(c.cutting_date)}</td>
                        <td className="px-3 py-2 font-medium text-blue-800">{c.design_number}</td>
                        <td className="px-3 py-2">{c.colour}</td>
                        <td className="px-3 py-2">{c.item_type}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.production_code}</td>
                        <td className="px-3 py-2">{c.cutting_sheet_number}</td>
                        <td className="px-3 py-2 text-right tabular font-semibold text-blue-800">{num(c.total_qty)}</td>
                        <td className="px-3 py-2 text-right tabular">{num(c.fabric_used_from_lot)}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
            </div>
            {history.production_orders?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Related Production Orders</p>
                <div className="flex flex-wrap gap-2">
                  {history.production_orders.map((o) => (
                    <span key={o.id} className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 text-xs font-medium border border-blue-100">{o.code} · {o.design_number}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!purchase} onClose={() => setPurchase(null)} title="Record Fabric Purchase" wide>
        {purchase && (
          <form onSubmit={submitPurchase} className="grid grid-cols-2 gap-4">
            <Field label="Mode" className="col-span-2">
              <Select value={purchase.add_to_existing ? "1" : "0"} onChange={(e) => { const on = e.target.value === "1"; setPurchase((p) => ({ ...p, add_to_existing: on, lot_number: "" })); }}
                options={[{ value: "0", label: "New lot — create a fresh lot number" }, { value: "1", label: "Add to existing lot — top up an existing lot" }]} data-testid="fabric-mode" />
            </Field>
            <Field label="Purchase Date"><Input type="date" value={purchase.receiving_date} onChange={(e) => setPurchase({ ...purchase, receiving_date: e.target.value })} /></Field>
            <Field label="Supplier">
              <Select value={purchase.supplier_name} onChange={(e) => setPurchase({ ...purchase, supplier_name: e.target.value })} placeholder="Select supplier"
                options={(suppliers || []).map((s) => ({ value: s.name, label: s.name }))} data-testid="fabric-purchase-supplier" />
            </Field>
            <Field label="Bill Number"><Input value={purchase.bill_number} onChange={(e) => setPurchase({ ...purchase, bill_number: e.target.value })} /></Field>
            {purchase.add_to_existing ? (
              <Field label="Existing Lot" required>
                <Select value={purchase.lot_number} onChange={(e) => pickLot(e.target.value)} placeholder="Select lot to top up"
                  options={(data || []).map((l) => ({ value: l.lot_number, label: `${l.lot_number} · ${l.fabric_type || ""} ${l.colour || ""} · bal ${num(l.balance_qty)} ${l.unit || ""}` }))} data-testid="fabric-existing-lot" />
              </Field>
            ) : (
              <Field label="Lot Number (optional)"><Input value={purchase.lot_number} onChange={(e) => setPurchase({ ...purchase, lot_number: e.target.value })} placeholder="Auto-generated if blank" /></Field>
            )}
            <Field label="Fabric Type" required><Input value={purchase.fabric_type} onChange={(e) => setPurchase({ ...purchase, fabric_type: e.target.value })} required data-testid="fabric-purchase-type" /></Field>
            <Field label="Colour"><Input value={purchase.colour} onChange={(e) => setPurchase({ ...purchase, colour: e.target.value })} /></Field>
            <Field label="Rolls"><Input type="number" step="any" value={purchase.rolls} onChange={(e) => setPurchase({ ...purchase, rolls: e.target.value })} /></Field>
            <Field label="Quantity" required><Input type="number" step="any" value={purchase.quantity} onChange={(e) => setPurchase({ ...purchase, quantity: e.target.value })} required data-testid="fabric-purchase-qty" /></Field>
            <Field label="Unit"><Select value={purchase.unit} onChange={(e) => setPurchase({ ...purchase, unit: e.target.value })} options={["MTR", "KG", "Roll"]} /></Field>
            <Field label="Rate"><Input type="number" step="any" value={purchase.rate} onChange={(e) => setPurchase({ ...purchase, rate: e.target.value })} /></Field>
            <Field label="Value"><Input readOnly className="bg-slate-50" value={money((parseFloat(purchase.quantity) || 0) * (parseFloat(purchase.rate) || 0))} /></Field>
            <Field label="Remarks" className="col-span-2"><Textarea value={purchase.remarks} onChange={(e) => setPurchase({ ...purchase, remarks: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setPurchase(null)}>Cancel</Button>
              <Button type="submit" data-testid="fabric-purchase-save">Record Purchase</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!loss} onClose={() => setLoss(null)} title={`Shift to Normal Loss — Lot ${loss?.lot?.lot_number || ""}`}>
        {loss && (
          <form onSubmit={submitLoss} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-amber-50 rounded p-2 text-sm">Balance: <b>{num(loss.lot.balance_qty)} {loss.lot.unit}</b> — this reduces the lot balance and logs a normal-loss ledger entry.</div>
            <Field label={`Loss Qty (${loss.lot.unit || "MTR"})`} required><Input type="number" step="any" value={loss.quantity} onChange={(e) => setLoss({ ...loss, quantity: e.target.value })} required data-testid="lot-loss-qty" /></Field>
            <Field label="Date"><Input type="date" value={loss.date} onChange={(e) => setLoss({ ...loss, date: e.target.value })} /></Field>
            <Field label="Reason / Remarks" className="col-span-2"><Textarea value={loss.reason} onChange={(e) => setLoss({ ...loss, reason: e.target.value })} placeholder="e.g. cutting wastage, shrinkage, damage" data-testid="lot-loss-reason" /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setLoss(null)}>Cancel</Button>
              <Button type="submit" data-testid="lot-loss-save">Shift to Loss</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!gain} onClose={() => setGain(null)} title={`Adjust with Gain — Lot ${gain?.lot?.lot_number || ""}`}>
        {gain && (
          <form onSubmit={submitGain} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-emerald-50 rounded p-2 text-sm">Balance: <b className="text-red-600">{num(gain.lot.balance_qty)} {gain.lot.unit}</b> — enter at least <b>{num(-(gain.lot.balance_qty || 0))}</b> to make it 0, or more to make it positive. New balance: <b data-testid="lot-gain-new-balance">{num(Math.round(((gain.lot.balance_qty || 0) + (parseFloat(gain.quantity) || 0)) * 100) / 100)}</b></div>
            <Field label={`Gain Qty (${gain.lot.unit || "MTR"})`} required><Input type="number" step="any" value={gain.quantity} onChange={(e) => setGain({ ...gain, quantity: e.target.value })} required data-testid="lot-gain-qty" /></Field>
            <Field label="Date"><Input type="date" value={gain.date} onChange={(e) => setGain({ ...gain, date: e.target.value })} data-testid="lot-gain-date" /></Field>
            <Field label="Reason / Remarks" className="col-span-2"><Textarea value={gain.reason} onChange={(e) => setGain({ ...gain, reason: e.target.value })} placeholder="e.g. extra metres found on recount" data-testid="lot-gain-reason" /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setGain(null)}>Cancel</Button>
              <Button type="submit" data-testid="lot-gain-save">Adjust with Gain</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit Lot ${edit?.lot_number || ""}`} wide>
        {edit && (
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-4">
            <Field label="Supplier">
              <Select value={edit.supplier_name || ""} onChange={(e) => setEdit({ ...edit, supplier_name: e.target.value })} placeholder="Select supplier"
                options={(suppliers || []).map((s) => ({ value: s.name, label: s.name }))} />
            </Field>
            <Field label="Bill Number"><Input value={edit.bill_number || ""} onChange={(e) => setEdit({ ...edit, bill_number: e.target.value })} /></Field>
            <Field label="Purchase Date"><Input type="date" value={edit.purchase_date || ""} onChange={(e) => setEdit({ ...edit, purchase_date: e.target.value })} /></Field>
            <Field label="Fabric Type"><Input value={edit.fabric_type || ""} onChange={(e) => setEdit({ ...edit, fabric_type: e.target.value })} /></Field>
            <Field label="Colour"><Input value={edit.colour || ""} onChange={(e) => setEdit({ ...edit, colour: e.target.value })} /></Field>
            <Field label="Rolls"><Input type="number" step="any" value={edit.rolls || ""} onChange={(e) => setEdit({ ...edit, rolls: e.target.value })} /></Field>
            <Field label="Balance Qty (stock correction)"><Input type="number" step="any" value={edit.balance_qty} onChange={(e) => setEdit({ ...edit, balance_qty: e.target.value })} data-testid="lot-edit-balance" /></Field>
            <Field label="Unit"><Select value={edit.unit || "MTR"} onChange={(e) => setEdit({ ...edit, unit: e.target.value })} options={["MTR", "KG", "Roll"]} /></Field>
            <Field label="Rate"><Input type="number" step="any" value={edit.rate || ""} onChange={(e) => setEdit({ ...edit, rate: e.target.value })} /></Field>
            <Field label="Remarks"><Input value={edit.remarks || ""} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" data-testid="lot-edit-save">Save Changes</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
